import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Trash2,
  Trash,
  Copy,
  BookOpen,
  StickyNote,
  Edit2,
  Save,
  X,
  Layers,
  FolderOpen,
} from "lucide-react";
import { useToasts } from "../hooks/useToasts";
import { articlesAPI } from "../services/api";
import Spinner from "../components/Spinner";
import Breadcrumbs from "../components/Breadcrumbs";
import StudyLayout from "../components/StudyLayout";
import NavigationButtons from "../components/NavigationButtons";
import "./UserFolders.css";

const UserFolders = () => {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteStep, setDeleteStep] = useState(0);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [editingFolderName, setEditingFolderName] = useState("");
  const [savingFolderPath, setSavingFolderPath] = useState(null);
  const { addToast } = useToasts();
  const navigate = useNavigate();
  const breadcrumbs = (
    <Breadcrumbs
      path={[
        { label: "MySpace", path: "/myspace" },
        {
          label: (
            <span className="user-folders-breadcrumb-label">
              <FolderOpen size={14} />
              <span>MyContent</span>
            </span>
          ),
          path: null,
        },
      ]}
    />
  );

  const fetchFolders = useCallback(async () => {
    setLoading(true);
    try {
      const [foldersResponse, contentFoldersResponse] = await Promise.all([
        articlesAPI.getFolders(),
        articlesAPI.getFolders(undefined, true),
      ]);

      const allFolderPaths = (foldersResponse.data.folders || []).map(
        (folder) => folder.name,
      );

      const contentFolders = new Set(
        (contentFoldersResponse.data.folders || [])
          .filter((folder) => folder.name)
          .map((folder) => folder.name),
      );

      const mapped = Array.from(contentFolders)
        .filter(
          (folderPath) =>
            !allFolderPaths.some(
              (candidatePath) => candidatePath.startsWith(`${folderPath} / `),
            ),
        )
        .sort((a, b) => a.localeCompare(b))
        .map((folderPath) => ({
          name: folderPath.includes(" / ")
            ? folderPath.split(" / ").pop()
            : folderPath,
          fullPath: folderPath,
          articleCount: 0,
        }));

      setFolders(mapped);
    } catch {
      addToast("Failed to load folders", "error-warning");
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  useEffect(() => {
    if (editingFolder) {
      // Delay to ensure textarea is rendered
      setTimeout(() => {
        const textarea = document.querySelector(".folder-name-textarea");
        if (textarea) {
          textarea.style.height = "auto";
          textarea.style.height = textarea.scrollHeight + "px";
        }
      }, 0);
    }
  }, [editingFolder]);

  const handleFolderClick = () => {
    // Disabled - do nothing
  };

  const handleTypeClick = (e, folder, type) => {
    e.stopPropagation();
    navigate(
      `/userspace?folder=${encodeURIComponent(folder.fullPath)}&type=${type}`,
    );
  };

  const handleDeleteClick = (e, folder) => {
    e.stopPropagation();
    setFolderToDelete(folder);
    setDeleteStep(1);
  };

  const handleCopyClick = async (e, folder) => {
    e.stopPropagation();
    try {
      const response = await articlesAPI.getArticlesByFolder(folder.fullPath);
      const articles = response.data.articles || [];
      const filteredArticles = articles.filter(
        a => a.body !== "Folder placeholder" && a.body !== "Subfolder placeholder"
      );
      const allText = filteredArticles.map((a) => a.body).join("\n\n");
      await navigator.clipboard.writeText(allText);
      addToast(`Copied ${filteredArticles.length} items to clipboard`, "success-info");
    } catch {
      addToast("Failed to copy content", "error-warning");
    }
  };

  const handleRenameClick = (e, folder) => {
    e.stopPropagation();
    setEditingFolder(folder);
    setEditingFolderName(folder.name);
  };

  const handleRenameFolder = async () => {
    if (!editingFolderName.trim()) {
      addToast("Folder name is required", "error-warning");
      return;
    }

    const normalizeString = (str) =>
      str
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    const toTitleCase = (str) =>
      str
        .trim()
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(" ");

    const normalizedNewName = normalizeString(editingFolderName.trim());
    const normalizedOldName = normalizeString(editingFolder.name);

    if (normalizedNewName === normalizedOldName) {
      setEditingFolder(null);
      setEditingFolderName("");
      return;
    }

    const folderExists = folders.some(
      (f) =>
        f.fullPath !== editingFolder.fullPath &&
        normalizeString(f.name) === normalizedNewName,
    );
    if (folderExists) {
      addToast("Folder already exists", "error-warning");
      return;
    }

    const finalFolderName = toTitleCase(editingFolderName.trim());
    const currentFolderPath = editingFolder.fullPath;

    try {
      setSavingFolderPath(currentFolderPath);
      const response = await articlesAPI.getArticlesByFolder(editingFolder.fullPath);
      const articles = response.data.articles || [];

      // Replace only the last part of the selected path and preserve subtree suffixes.
      const pathParts = editingFolder.fullPath.split(' / ');
      pathParts[pathParts.length - 1] = finalFolderName;
      const newFullPath = pathParts.join(' / ');
      const oldPrefix = editingFolder.fullPath;
      const escapedOldPrefix = oldPrefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const prefixRegex = new RegExp(`^${escapedOldPrefix}(?: / )?`);

      await Promise.all(
        articles.map((article) => {
          const suffix = article.title.replace(prefixRegex, "");
          const nextTitle = suffix ? `${newFullPath} / ${suffix}` : newFullPath;
          return articlesAPI.updateArticle(article._id, {
            title: nextTitle,
            author: article.author,
            chapter: article.chapter,
            body: article.body,
          });
        }),
      );

      await fetchFolders();
      addToast(`Folder renamed to "${finalFolderName}"`, "success-info");
      setEditingFolder(null);
      setEditingFolderName("");
    } catch {
      addToast("Failed to rename folder", "error-warning");
    } finally {
      setSavingFolderPath(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingFolder(null);
    setEditingFolderName("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleRenameFolder();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteStep === 1) {
      setDeleteStep(2);
      return;
    }

    if (deleteStep === 2) {
      setDeleting(true);
      try {
        const response = await articlesAPI.getArticlesByFolder(folderToDelete.fullPath);
        const articles = response.data.articles || [];
        const articleIds = articles.map((a) => a._id);
        const realCount = articles.filter(
          (a) => a.body !== "Folder placeholder" && a.body !== "Subfolder placeholder"
        ).length;

        await Promise.all(
          articleIds.map((id) => articlesAPI.deleteArticle(id)),
        );

        await fetchFolders();
        addToast(
          realCount > 0
            ? `Folder "${folderToDelete.name}" and ${realCount} items deleted`
            : `Folder "${folderToDelete.name}" deleted`,
          "success-info",
        );
      } catch {
        addToast("Failed to delete folder", "error-warning");
      } finally {
        setDeleting(false);
        setDeleteStep(0);
        setFolderToDelete(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteStep(0);
    setFolderToDelete(null);
  };



  if (loading) return (
    <StudyLayout
      breadcrumbs={breadcrumbs}
      buttons={<NavigationButtons />}
    >
      <Spinner visible={true} />
    </StudyLayout>
  );

  return (
    <StudyLayout
      breadcrumbs={breadcrumbs}
      buttons={<NavigationButtons />}
    >
      <div className="user-folders-page">
        {folders.length === 0 ? (
          <div className="user-folders-empty-state">
            No folders yet. Create folders from My Space.
          </div>
        ) : (
          <div className="user-folders-grid">
            {folders.map((folder) => {
              const isTruncated = folder.name.length > 30;
              return (
                <div
                  key={folder.fullPath}
                  className="user-folder-card"
                  onClick={(e) => handleFolderClick(folder.name, e)}
                  title="Select an icon above"
                >
                  <div className="folder-type-icons">
                    <button
                      className="folder-type-icon folder-type-lesson"
                      onClick={(e) => handleTypeClick(e, folder, "lesson")}
                      title="Lessons"
                    >
                      <BookOpen size={27} />
                    </button>
                    <button
                      className="folder-type-icon folder-type-note"
                      onClick={(e) => handleTypeClick(e, folder, "note")}
                      title="Notes"
                    >
                      <StickyNote size={27} />
                    </button>
                    <button
                      className="folder-type-icon folder-type-flash"
                      onClick={(e) =>
                        handleTypeClick(e, folder, "flashcard")
                      }
                      title="Flashcards"
                    >
                      <Layers size={27} />
                    </button>
                  </div>
                  <div
                    className="user-folder-name"
                    title={isTruncated ? folder.name : undefined}
                  >
                    {editingFolder && editingFolder.fullPath === folder.fullPath ? (
                      <textarea
                        value={editingFolderName}
                        onChange={(e) => setEditingFolderName(e.target.value)}
                        onKeyDown={handleKeyPress}
                        className="folder-name-textarea"
                        autoFocus
                        maxLength="80"
                        onClick={(e) => e.stopPropagation()}
                        rows="1"
                        onInput={(e) => {
                          e.target.style.height = "auto";
                          e.target.style.height = e.target.scrollHeight + "px";
                        }}
                      />
                    ) : (
                      folder.name
                    )}
                  </div>
                  <div className="user-folder-info">
                    <div className="user-folder-actions">
                      <button
                        className={`user-folder-edit-btn ${editingFolder && editingFolder.fullPath === folder.fullPath ? "save-mode" : ""}`}
                        disabled={savingFolderPath === folder.fullPath}
                        onClick={(e) => {
                          if (
                            editingFolder &&
                            editingFolder.fullPath === folder.fullPath
                          ) {
                            e.stopPropagation();
                            handleRenameFolder();
                          } else {
                            handleRenameClick(e, folder);
                          }
                        }}
                        title={
                          editingFolder && editingFolder.fullPath === folder.fullPath
                            ? "Save changes"
                            : "Rename folder"
                        }
                      >
                        {editingFolder && editingFolder.fullPath === folder.fullPath ? (
                          savingFolderPath === folder.fullPath ? (
                            <span className="user-folder-saving-text">...</span>
                          ) : (
                            <Save size={18} />
                          )
                        ) : (
                          <Edit2 size={18} />
                        )}
                      </button>
                      <button
                        className="user-folder-copy-btn"
                        onClick={(e) => handleCopyClick(e, folder)}
                        title="Copy all inside"
                      >
                        <Copy size={18} />
                      </button>
                      <button
                        className="user-folder-delete-btn"
                        onClick={(e) => handleDeleteClick(e, folder)}
                        title="Delete folder and all inside"
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  </div>

                  {deleteStep > 0 && folderToDelete?.fullPath === folder.fullPath && (
                    <div className="delete-confirmation-in-card">
                      <div className="delete-confirmation-content">
                        <div className="delete-text">
                          {deleteStep === 1
                            ? `Delete folder and all inside?`
                            : "Delete everything? Cannot be undone!"}
                        </div>
                        <div className="delete-actions">
                          <button
                            className="delete-confirm-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteConfirm();
                            }}
                            disabled={deleting}
                            title={deleteStep === 1 ? "Delete" : "Yes, Delete"}
                          >
                            {deleting ? (
                              <span className="user-folder-deleting-text">...</span>
                            ) : (
                              <Trash2 size={14} />
                            )}
                          </button>
                          <button
                            className="delete-cancel-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCancel();
                            }}
                            title="Cancel"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </StudyLayout>
  );
};

export default UserFolders;
